import { useState } from "react";

import { ArrowDownTrayIcon, ArrowPathIcon, SparklesIcon } from "@heroicons/react/24/outline";

import usePartAiTitle from "../../data/usePartAiTitle";
import { IPart, IPartAiTitleResult } from "../../models/Part";
import classNames from "../../utils/classNames";
import useNotification from "../notifications/useNotifications";

interface AiTitleGeneratorProps {
  part: IPart;
  onApply: (title: string) => void;
  disabled?: boolean;
}

const scoreClasses = (score: number) => {
  if (score >= 80) return "bg-emerald-100 text-emerald-700";
  if (score >= 60) return "bg-amber-100 text-amber-700";
  return "bg-red-100 text-red-700";
};

export default function AiTitleGenerator({ part, onApply, disabled = false }: AiTitleGeneratorProps) {
  const { generateAiTitle } = usePartAiTitle();
  const { addNotification } = useNotification();

  const [result, setResult] = useState<IPartAiTitleResult | null>(null);

  const handleGenerate = () => {
    generateAiTitle.mutate(part.id, {
      onSuccess: (response) => {
        if (response.success && response.data) {
          setResult(response.data);
        } else {
          addNotification({
            variant: "error",
            primaryText: response.message ?? "Unable to generate AI title",
          });
        }
      },
      onError: (error: any) => {
        addNotification({
          variant: "error",
          primaryText: error?.message ?? "Unable to generate AI title",
        });
      },
    });
  };

  const isGenerating = generateAiTitle.isLoading;

  // Rendered as a fragment so the button can sit inline in a flex row;
  // the result panel is w-full so it wraps onto its own line in a flex-wrap container.
  return (
    <>
      {!result && (
        <button
          type="button"
          onClick={handleGenerate}
          disabled={disabled || isGenerating}
          title="Generate an AI-optimised eBay title for this part"
          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-violet-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-violet-700 transition hover:bg-violet-50 disabled:opacity-60"
        >
          <SparklesIcon className={classNames("h-3.5 w-3.5", isGenerating && "animate-pulse")} />
          {isGenerating ? "Generating..." : "Generate AI Title"}
        </button>
      )}

      {result && (
        <div className="w-full rounded-md border border-violet-200 bg-violet-50/40 p-2.5 text-xs text-gray-700">
          <div className="flex items-center gap-2">
            <SparklesIcon className="h-4 w-4 shrink-0 text-violet-600" />
            <span className="min-w-0 flex-1 break-words font-medium text-gray-900">{result.title}</span>
            <span
              className="shrink-0 tabular-nums text-[11px] text-violet-700/80"
              title="Characters in the title (eBay max 80)"
            >
              {result.characterCount}/80
            </span>
            <span
              className={classNames("shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold", scoreClasses(result.score))}
              title="AI title score out of 100"
            >
              {result.score}/100
            </span>
          </div>

          {result.removedData.length > 0 && (
            <p className="mt-1.5 text-[11px] text-gray-600">
              <span className="font-semibold">Removed to fit: </span>
              {result.removedData.join(", ")}
            </p>
          )}

          {result.warnings.length > 0 && (
            <p className="mt-1 text-[11px] text-amber-700">
              <span className="font-semibold">Warnings: </span>
              {result.warnings.join(", ")}
            </p>
          )}

          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                onApply(result.title);
                setResult(null);
              }}
              disabled={disabled}
              title="Use this AI title as the listing title"
              className="inline-flex items-center gap-1 rounded-md border border-violet-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-violet-700 transition hover:bg-violet-50 disabled:opacity-60"
            >
              <ArrowDownTrayIcon className="h-3.5 w-3.5" />
              Use AI title
            </button>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={disabled || isGenerating}
              title="Generate a new AI title"
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-gray-600 transition hover:bg-gray-50 disabled:opacity-60"
            >
              <ArrowPathIcon className={classNames("h-3.5 w-3.5", isGenerating && "animate-spin")} />
              Regenerate
            </button>
          </div>
        </div>
      )}
    </>
  );
}
